import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing query state with localStorage persistence
 * Replaces Redux query state with local state following app's standard pattern
 * 
 * Persists: mode, filterPills, quickFilter, inputText, sortField, sortOrder
 * State is project-specific - each project has its own persisted filters
 */
const getStorageKey = (projectId) => {
  return projectId ? `testcase_query_state_${projectId}` : 'testcase_query_state';
};

const getDefaultState = () => ({
  mode: 'ui',
  filterPills: [],
  quickFilter: '',
  inputText: '',
  sortField: 'created_at',
  sortOrder: 'desc'
});

const loadStateFromStorage = (projectId) => {
  try {
    const storageKey = getStorageKey(projectId);
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);    
      return { ...getDefaultState(), ...parsed };
    }
  } catch (error) {
    console.error('Error loading query state from localStorage:', error);
  }
  return getDefaultState();
};

const saveStateToStorage = (state, projectId) => {
  try {
    const storageKey = getStorageKey(projectId);
    const stateToPersist = {
      mode: state.mode,
      filterPills: state.filterPills,
      quickFilter: state.quickFilter,
      inputText: state.inputText,
      sortField: state.sortField,
      sortOrder: state.sortOrder
    };
    localStorage.setItem(storageKey, JSON.stringify(stateToPersist));
  } catch (error) {
    console.error('Error saving query state to localStorage:', error);
  }
};

export const useQueryState = (projectId = null) => {
  const [state, setState] = useState(() => loadStateFromStorage(projectId));

  // Save to localStorage whenever state or projectId changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveStateToStorage(state, projectId);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [state, projectId]);
  
  // Reload state when projectId changes
  useEffect(() => {
    if (projectId) {
      const newState = loadStateFromStorage(projectId);
      setState(newState);
    }
  }, [projectId]);

  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const setMode = useCallback((mode) => {
    setState(prev => ({ ...prev, mode }));
  }, []);

  const setFilterPills = useCallback((filterPills) => {
    setState(prev => ({ ...prev, filterPills }));
  }, []);

  const setQuickFilter = useCallback((quickFilter) => {
    setState(prev => ({ ...prev, quickFilter }));
  }, []);

  const setInputText = useCallback((inputText) => {
    setState(prev => ({ ...prev, inputText }));
  }, []);

  const setSort = useCallback(({ field, order }) => {
    setState(prev => ({ ...prev, sortField: field, sortOrder: order }));
  }, []);

  const setSortField = useCallback((sortField) => {
    setState(prev => ({ ...prev, sortField }));
  }, []);

  const setSortOrder = useCallback((sortOrder) => {
    setState(prev => ({ ...prev, sortOrder }));
  }, []);

  // Clear filters (but keep mode and sort)
  const clearFilters = useCallback(() => {
    setState(prev => ({
      ...prev,
      filterPills: [],
      quickFilter: '',
      inputText: ''
    }));
  }, []);

  const reset = useCallback(() => {
    const defaults = getDefaultState();
    setState(defaults);
    if (projectId) {
      localStorage.removeItem(getStorageKey(projectId));
    }
  }, [projectId]);

  return {
    ...state,
    setMode,
    setFilterPills,
    setQuickFilter,
    setInputText,
    setSort,
    setSortField,
    setSortOrder,
    clearFilters,
    reset,
    updateState
  };
};

